import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import useLenders from '@/hooks/use-lenders';
import { cn, pluralize } from '@/lib/utils';
import type { Lender } from '@/models/types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useFilteredDealsContext } from '../filtered-deals/filtered-deals-provider';
import type { LenderFilters } from '../types';

const LenderFiltersComboBoxPopover = ({
  children,
  comboBoxOnChange,
  comboBoxOpen,
  onOpenChange,
  filters: lenderFilters,
  stayOpenAfterSelection = true,
  alignOffset,
  side = 'bottom',
  align,
  popoverContentClassName,
}: {
  children: ReactNode;
  comboBoxOnChange: (value: string) => void;
  comboBoxOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: LenderFilters;
  stayOpenAfterSelection: boolean;
  alignOffset?: number;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  popoverContentClassName?: string;
}) => {
  const { lenders } = useLenders();
  const { getCount } = useFilteredDealsContext();

  const comboBoxOptions = useMemo(() => {
    return lenders.map((lender) => {
      const count = getCount('lender', lender.id);

      return {
        label: (
          <div className="flex flex-row items-center gap-2 text-nowrap">
            {lender.name}
            <span className="text-xs text-gray-400">
              {count} {pluralize(count, 'deal', 'deals')}
            </span>
          </div>
        ),
        value: lender.id,
        icon: (
          <div className="size-4">
            <Checkbox checked={lenderFilters[lender.id as Lender['id']]} />
          </div>
        ),
      };
    });
  }, [lenderFilters, getCount]);

  return (
    <ComboboxPopover
      placeholder={'Status...'}
      options={comboBoxOptions}
      onChange={comboBoxOnChange}
      align={align}
      side={side}
      alignOffset={alignOffset}
      // TODO: Add end adornment and empty message
      endAdornment={undefined}
      emptyMessage={undefined}
      open={comboBoxOpen}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={stayOpenAfterSelection}
      popoverContentClassName={cn(
        'w-fit min-w-[200px]',
        popoverContentClassName,
      )}
    >
      {children}
    </ComboboxPopover>
  );
};

export default LenderFiltersComboBoxPopover;
